import { Injectable, signal } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Network } from '@capacitor/network';
import { isOfflineCapableNative } from '../commons/utils/platform';

// navigator.onLine reflects OS-level connectivity, not actual server reachability;
// a heartbeat-probe refinement lands with the Capacitor shell (offline lease work).
@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private readonly onlineSignal = signal<boolean>(navigator.onLine);
  readonly online = this.onlineSignal.asReadonly();

  // Bumped once per settled offline->online transition (debounced — see handleOnlineEvent).
  // Consumers (OfflinePrefetchService) react to ANY change in this counter via effect(), not
  // to a specific value — a plain boolean "pulse" (true then immediately false) risks two
  // effects reading it in the same tick racing each other; an incrementing counter has no such
  // ambiguity. SyncEngineService deliberately does NOT use this — it reacts to the raw
  // `online()` signal instead, since a queued offline write should replay the instant
  // connectivity returns, not after this signal's debounce window.
  private readonly onlineTransitionTickSignal = signal(0);
  readonly onlineTransitionTick = this.onlineTransitionTickSignal.asReadonly();

  // Absorbs connection flapping on a spotty mobile connection — a full-app sync shouldn't
  // restart every time a brief drop-and-recover happens. Not a "don't sync too often" cooldown
  // (that's explicitly what this replaces elsewhere); purely "wait until the transition looks
  // settled" before treating it as real.
  private static readonly STABLE_ONLINE_DEBOUNCE_MS = 8000;
  private debounceHandle: ReturnType<typeof setTimeout> | null = null;

  // Confirmed live 2026-08-05: a real reconnect can flap offline->online->offline->online
  // within a handful of seconds (radio re-associating, DHCP, etc.) before genuinely settling.
  // Without this, EVERY online edge independently re-armed its own fresh 8s debounce (the old
  // handleOfflineEvent() below used to cancel the pending timer + drop the "already handling a
  // transition" state on ANY offline blip, no matter how brief) — observed as two complete
  // OfflinePrefetchService sync batches firing ~7.7s apart from one physical reconnect.
  // offlineSince tracks how long the CURRENT offline stretch has actually lasted; only a
  // stretch of at least MIN_REAL_OFFLINE_MS counts as a real disconnect worth re-arming the
  // debounce when it ends — a shorter blip is ignored entirely (any already-pending timer from
  // an earlier real transition is left running undisturbed; its own `if (this.onlineSignal())`
  // check at fire-time already skips ticking if we're still offline right then).
  private static readonly MIN_REAL_OFFLINE_MS = 3000;
  private offlineSince: number | null = null;

  constructor() {
    window.addEventListener('online', () => this.handleOnlineEvent());
    window.addEventListener('offline', () => this.handleOfflineEvent());
    this.watchNativeNetwork();
    this.initSlowConnectionWatch();
  }

  // OFFLINE_PLAN.md 1.4 (2026-09-29). Inside the phone app, the WebView's navigator.onLine is often
  // late or wrong (e.g. stays "online" after Wi-Fi drops); the phone's own network status is the real
  // signal. Both feed the same handlers below, which are safe to call twice for the same change.
  private watchNativeNetwork(): void {
    if (!isOfflineCapableNative()) return;
    try {
      Network.getStatus()
        .then((status) => status.connected ? this.handleOnlineEvent() : this.handleOfflineEvent())
        .catch(() => { /* plugin missing on an old app build — navigator.onLine still works */ });
      Network.addListener('networkStatusChange', (status) => {
        if (status.connected) this.handleOnlineEvent(); else this.handleOfflineEvent();
      }).catch(() => undefined);
    } catch { /* same as above */ }
  }

  private handleOnlineEvent(): void {
    const wasReallyOffline = this.offlineSince !== null
      && (Date.now() - this.offlineSince) >= NetworkStatusService.MIN_REAL_OFFLINE_MS;
    this.onlineSignal.set(true);
    this.offlineSince = null;
    if (!wasReallyOffline) return; // brief blip, not a settled transition — don't re-arm
    if (this.debounceHandle) clearTimeout(this.debounceHandle);
    this.debounceHandle = setTimeout(() => {
      this.debounceHandle = null;
      if (this.onlineSignal()) { // still online after the debounce window — settled, not a blip
        this.onlineTransitionTickSignal.update((v) => v + 1);
      }
    }, NetworkStatusService.STABLE_ONLINE_DEBOUNCE_MS);
  }

  private handleOfflineEvent(): void {
    this.onlineSignal.set(false);
    if (this.offlineSince === null) this.offlineSince = Date.now();
    // Deliberately NOT cancelling a pending debounce timer here anymore — see the comment on
    // offlineSince above. A brief blip must not restart another full 8s wait.
  }

  get isOnline(): boolean {
    return this.onlineSignal();
  }

  get isSlowConnection(): boolean {
    const connection = (navigator as any).connection;
    return !!connection && ['slow-2g', '2g'].includes(connection.effectiveType);
  }

  // Reactive slow-connection detection, combining two independent signals:
  // 1) navigator.connection.effectiveType — instant, but Chromium-only; Safari/Firefox never
  //    populate `navigator.connection` at all, so this alone misses roughly half of real users.
  // 2) A rolling average of actual GET request latency, sampled by the HTTP interceptor via
  //    recordRequestLatency() — works in every browser, and also catches a connection that
  //    *reports* as fast (4G) but is currently congested/throttled, which effectiveType alone
  //    can't see. This is the signal SlowConnectionIndicatorComponent renders from.
  private readonly slowConnectionSignal = signal<boolean>(false);
  readonly slowConnection = this.slowConnectionSignal.asReadonly();

  // Hysteresis: a different (lower) threshold to CLEAR the flag than to SET it, so a connection
  // hovering right at the boundary doesn't flap the indicator on and off every other request.
  private static readonly SLOW_LATENCY_THRESHOLD_MS = 2500;
  private static readonly RECOVERY_LATENCY_THRESHOLD_MS = 1200;
  private static readonly LATENCY_SAMPLE_WINDOW = 5;
  private static readonly MIN_SAMPLES_BEFORE_LATENCY_SIGNAL = 3;
  private latencySamples: number[] = [];

  // 2026-09-15 — "Slow connection" was showing on a 100 Mbps line. Request latency includes the
  // SERVER's time to answer (Command Center, a 529-row tally…), so a slow screen looked like a slow
  // network. Before showing the message on latency alone, a tiny public health check
  // (/actuator/health/liveness, ~15 bytes, no auth, no database) is timed directly. If it answers
  // quickly the network is fine: the message stays hidden and latency is ignored for a minute.
  // Only a slow or failed probe (while the device says it is online) confirms a slow connection.
  // effectiveType 2g/slow-2g still shows it immediately, as before.
  private static readonly PROBE_FAST_MS = 1500;
  private static readonly PROBE_TIMEOUT_MS = 8000;
  private static readonly PROBE_COOLDOWN_MS = 60000;
  private probeInFlight = false;
  private lastProbeAt = 0;
  private serverSlowUntil = 0;

  private initSlowConnectionWatch(): void {
    const connection = (navigator as any).connection;
    connection?.addEventListener?.('change', () => this.recomputeSlowConnection());
    this.recomputeSlowConnection();
  }

  // Called by TallysheetHttpInterceptorService after every successful (non-error) GET response.
  // Deliberately fed only non-error, non-long-running requests (export/import/report/etc. are
  // expected to take a while and would otherwise constantly trip this) — a failed/timed-out
  // request is a connectivity problem surfaced elsewhere (the offline banner, the 500 dialog),
  // not a "slow but working" one.
  recordRequestLatency(ms: number): void {
    this.latencySamples.push(ms);
    if (this.latencySamples.length > NetworkStatusService.LATENCY_SAMPLE_WINDOW) {
      this.latencySamples.shift();
    }
    this.recomputeSlowConnection();
  }

  private recomputeSlowConnection(): void {
    const connection = (navigator as any).connection;
    const byConnectionType = !!connection && ['slow-2g', '2g'].includes(connection.effectiveType);

    if (this.latencySamples.length < NetworkStatusService.MIN_SAMPLES_BEFORE_LATENCY_SIGNAL) {
      this.slowConnectionSignal.set(byConnectionType);
      return;
    }
    const avgLatency = this.latencySamples.reduce((a, b) => a + b, 0) / this.latencySamples.length;
    const threshold = this.slowConnectionSignal()
      ? NetworkStatusService.RECOVERY_LATENCY_THRESHOLD_MS
      : NetworkStatusService.SLOW_LATENCY_THRESHOLD_MS;
    const latencySlow = avgLatency > threshold;
    if (byConnectionType) { this.slowConnectionSignal.set(true); return; }
    if (this.slowConnectionSignal()) { this.slowConnectionSignal.set(latencySlow); return; } // recovery, as before
    if (!latencySlow || Date.now() < this.serverSlowUntil) { this.slowConnectionSignal.set(false); return; }
    this.probeNetwork();
  }

  /** Times the tiny health check to tell a slow NETWORK from a slow SERVER (see fields above).
   *  Plain fetch (not HttpClient) so it never feeds back into recordRequestLatency, with no-cors so
   *  no CORS setup is needed — only the timing matters, not the body. */
  private probeNetwork(): void {
    const now = Date.now();
    if (this.probeInFlight || now - this.lastProbeAt < NetworkStatusService.PROBE_COOLDOWN_MS) return;
    if (!navigator.onLine || typeof fetch !== 'function') return;
    this.probeInFlight = true;
    this.lastProbeAt = now;
    const base = (environment as any).apiUrl ?? '';
    const url = `${String(base).replace(/\/+$/, '')}/actuator/health/liveness?t=${now}`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => controller?.abort(), NetworkStatusService.PROBE_TIMEOUT_MS);
    const started = performance.now();
    fetch(url, { mode: 'no-cors', cache: 'no-store', credentials: 'omit', signal: controller?.signal })
      .then(() => {
        const ms = performance.now() - started;
        if (ms < NetworkStatusService.PROBE_FAST_MS) {
          // Network is fine — the server was just slow on some requests. Don't blame the connection.
          this.serverSlowUntil = Date.now() + NetworkStatusService.PROBE_COOLDOWN_MS;
          this.latencySamples = [];
          this.slowConnectionSignal.set(false);
        } else {
          this.slowConnectionSignal.set(true);
        }
      })
      .catch(() => {
        // Aborted (took over 8s) or failed while the device reports online: genuinely poor connection.
        if (navigator.onLine) this.slowConnectionSignal.set(true);
      })
      .finally(() => { clearTimeout(timer); this.probeInFlight = false; });
  }
}
